
import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import DeviceStatus from './DeviceStatus';
import { Button } from '@/components/ui/button';
import { AlertCircle, MoreHorizontal, Settings, RefreshCw, Info, X, Shield } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { extendedDeviceData } from '@/data/mockHierarchy';
import { useHierarchy } from '@/contexts/HierarchyContext';
import { Badge } from '@/components/ui/badge';

interface DeviceTableProps {
  className?: string;
  searchQuery?: string;
  hierarchyFilters?: {
    organization?: string;
    department?: string;
    location?: string;
    accessLevel?: string;
    deviceType?: string;
  };
}

const DeviceTable: React.FC<DeviceTableProps> = ({ 
  className, 
  searchQuery = '',
  hierarchyFilters = {} 
}) => {
  const isMobile = useMobile();
  const { toast } = useToast();
  const { canAccess } = useHierarchy();
  const [isConfigureSheetOpen, setIsConfigureSheetOpen] = useState(false);
  const [isDetailsSheetOpen, setIsDetailsSheetOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  
  // Filter devices based on search query and hierarchy filters
  const filteredDevices = extendedDeviceData.filter(device => {
    // First check if user has access to this device's security level
    if (!canAccess(device.accessLevel as any)) {
      return false;
    }
    
    // Then apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!(
        device.id.toLowerCase().includes(query) ||
        device.name.toLowerCase().includes(query) ||
        device.type.toLowerCase().includes(query) ||
        device.ip.toLowerCase().includes(query) ||
        device.location.toLowerCase().includes(query) ||
        device.user.toLowerCase().includes(query)
      )) {
        return false;
      }
    }
    
    // Apply hierarchy filters
    if (hierarchyFilters.organization && device.organizationId !== hierarchyFilters.organization) {
      return false;
    }
    if (hierarchyFilters.department && device.departmentId !== hierarchyFilters.department) {
      return false;
    }
    if (hierarchyFilters.location && device.locationId !== hierarchyFilters.location) {
      return false;
    }
    if (hierarchyFilters.accessLevel && device.accessLevel !== hierarchyFilters.accessLevel) {
      return false;
    }
    if (hierarchyFilters.deviceType && device.type !== hierarchyFilters.deviceType) {
      return false;
    }
    
    return true;
  });

  // Clean up function to ensure proper state reset when sheet closes
  useEffect(() => {
    if (!isConfigureSheetOpen && !isDetailsSheetOpen) {
      // Small delay to ensure proper cleanup after animations
      const timer = setTimeout(() => {
        // We don't reset selectedDevice immediately to avoid UI flicker during animations
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isConfigureSheetOpen, isDetailsSheetOpen]);

  const handleConfigure = (device: any) => {
    setSelectedDevice(device);
    setIsConfigureSheetOpen(true);
  };

  const handleRestart = (device: any) => {
    toast({
      title: "Restarting device",
      description: `${device.name} (${device.id}) is restarting. This may take a few moments.`,
      duration: 5000,
    });
    
    // In a real application, this would send an API request to restart the device
    setTimeout(() => {
      toast({
        title: "Device restarted",
        description: `${device.name} (${device.id}) has been successfully restarted.`,
        duration: 3000,
      });
    }, 3000);
  };

  const handleViewDetails = (device: any) => {
    setSelectedDevice(device);
    setIsDetailsSheetOpen(true);
  };

  const handleRemoveDevice = (device: any) => {
    toast({
      title: "Device removed",
      description: `${device.name} (${device.id}) has been removed from the network.`,
      variant: "destructive",
      duration: 3000,
    });
    // In a real application, this would send an API request to remove the device
  };

  const handleCloseConfigureSheet = () => {
    setIsConfigureSheetOpen(false);
  };

  const handleCloseDetailsSheet = () => {
    setIsDetailsSheetOpen(false);
  };

  const getAccessLevelBadge = (level: string) => {
    const colors: Record<string, string> = {
      'top-secret': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
      'secret': 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
      'confidential': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
      'restricted': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
      'unclassified': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
    };
    
    return (
      <Badge className={colors[level]} variant="outline">
        <span className="capitalize">{level}</span>
      </Badge>
    );
  };

  return (
    <div className={`${className} overflow-x-auto -mx-4 sm:mx-0`}>
      {filteredDevices.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No devices found matching your search or filters. You may not have access to view some devices.
        </div>
      ) : (
        <div className="min-w-full px-4 sm:px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Device</TableHead>
                <TableHead className="hidden md:table-cell">Location</TableHead>
                {!isMobile && <TableHead className="hidden lg:table-cell">Type</TableHead>}
                {!isMobile && <TableHead className="hidden md:table-cell">Security</TableHead>}
                {!isMobile && <TableHead className="hidden lg:table-cell">IP Address</TableHead>}
                {!isMobile && <TableHead className="hidden lg:table-cell">User</TableHead>}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDevices.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>
                    <DeviceStatus status={device.status as 'online' | 'offline' | 'warning'} />
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{device.name}</div>
                      <div className="text-xs text-muted-foreground">{device.id}</div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{device.location}</TableCell>
                  {!isMobile && <TableCell className="hidden lg:table-cell">{device.type}</TableCell>}
                  {!isMobile && (
                    <TableCell className="hidden md:table-cell">
                      {getAccessLevelBadge(device.accessLevel)}
                    </TableCell>
                  )}
                  {!isMobile && <TableCell className="hidden lg:table-cell text-muted-foreground">{device.ip}</TableCell>}
                  {!isMobile && <TableCell className="hidden lg:table-cell">{device.user}</TableCell>}
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleConfigure(device)}>
                          <Settings className="h-4 w-4 mr-2" />
                          Configure
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleRestart(device)}>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Restart
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewDetails(device)}>
                          <Info className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-destructive focus:text-destructive"
                          onClick={() => handleRemoveDevice(device)}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Configure Device Sheet */}
      <Sheet open={isConfigureSheetOpen} onOpenChange={handleCloseConfigureSheet}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Configure Device</SheetTitle>
            <SheetDescription>
              {selectedDevice && `Configure settings for ${selectedDevice.name} (${selectedDevice.id})`}
            </SheetDescription>
          </SheetHeader>
          {selectedDevice && (
            <div className="space-y-4 py-6">
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Basic Information</h3>
                <div className="grid gap-3">
                  <div className="grid gap-1">
                    <label htmlFor="device-name" className="text-sm font-medium">Device Name</label>
                    <Input id="device-name" defaultValue={selectedDevice.name} />
                  </div>
                  <div className="grid gap-1">
                    <label htmlFor="device-location" className="text-sm font-medium">Location</label>
                    <Select defaultValue={selectedDevice.locationId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a location" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedDevice.location ? (
                          <SelectItem value={selectedDevice.locationId}>{selectedDevice.location}</SelectItem>
                        ) : (
                          <>
                            <SelectItem value="loc-001">Pentagon - East Wing</SelectItem>
                            <SelectItem value="loc-002">Fort Meade - Building A</SelectItem>
                            <SelectItem value="loc-003">DC Headquarters - 4th Floor</SelectItem>
                            <SelectItem value="loc-004">Acme Tower - Server Room</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1">
                    <label htmlFor="security-level" className="text-sm font-medium">Security Classification</label>
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      {getAccessLevelBadge(selectedDevice.accessLevel)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Security classification can only be changed by a Security Officer
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Network Settings</h3>
                <div className="grid gap-3">
                  <div className="grid gap-1">
                    <label htmlFor="device-ip" className="text-sm font-medium">IP Address</label>
                    <Input id="device-ip" defaultValue={selectedDevice.ip} />
                  </div>
                  <div className="grid gap-1">
                    <label htmlFor="device-subnet" className="text-sm font-medium">Subnet Mask</label>
                    <Input id="device-subnet" defaultValue="255.255.255.0" />
                  </div>
                  <div className="grid gap-1">
                    <label htmlFor="device-gateway" className="text-sm font-medium">Gateway</label>
                    <Input id="device-gateway" defaultValue="192.168.1.1" />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Device Specific Settings</h3>
                <div className="grid gap-3">
                  <div className="grid gap-1">
                    <label htmlFor="update-interval" className="text-sm font-medium">Update Interval (minutes)</label>
                    <Input id="update-interval" type="number" defaultValue="5" />
                  </div>
                  <div className="grid gap-1">
                    <label htmlFor="power-mode" className="text-sm font-medium">Power Mode</label>
                    <Select defaultValue="balanced">
                      <SelectTrigger>
                        <SelectValue placeholder="Select power mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low-power">Low Power</SelectItem>
                        <SelectItem value="balanced">Balanced</SelectItem>
                        <SelectItem value="performance">Performance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <SheetClose asChild>
                  <Button variant="outline">Cancel</Button>
                </SheetClose>
                <Button onClick={() => {
                  toast({
                    title: "Settings saved",
                    description: `${selectedDevice.name} (${selectedDevice.id}) settings have been updated.`,
                    duration: 3000,
                  });
                  setIsConfigureSheetOpen(false);
                }}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* View Details Sheet */}
      <Sheet open={isDetailsSheetOpen} onOpenChange={handleCloseDetailsSheet}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Device Details</SheetTitle>
            <SheetDescription>
              {selectedDevice && `Details for ${selectedDevice.name} (${selectedDevice.id})`}
            </SheetDescription>
          </SheetHeader>
          {selectedDevice && (
            <div className="space-y-4 py-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-1">Status</h3>
                  <div className="flex items-center">
                    <DeviceStatus status={selectedDevice.status as 'online' | 'offline' | 'warning'} />
                    <span className="ml-2 text-sm capitalize">{selectedDevice.status}</span>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium mb-1">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">ID</div>
                    <div>{selectedDevice.id}</div>
                    <div className="text-muted-foreground">Name</div>
                    <div>{selectedDevice.name}</div>
                    <div className="text-muted-foreground">Type</div>
                    <div>{selectedDevice.type}</div>
                    <div className="text-muted-foreground">Last Seen</div>
                    <div>{selectedDevice.lastSeen}</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium mb-1">Hierarchy Information</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">Organization</div>
                    <div>{selectedDevice.organizationId}</div>
                    <div className="text-muted-foreground">Department</div>
                    <div>{selectedDevice.departmentId}</div>
                    <div className="text-muted-foreground">Location</div>
                    <div>{selectedDevice.location}</div>
                    <div className="text-muted-foreground">Security Level</div>
                    <div>{getAccessLevelBadge(selectedDevice.accessLevel)}</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium mb-1">Network Information</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">IP Address</div>
                    <div>{selectedDevice.ip}</div>
                    <div className="text-muted-foreground">MAC Address</div>
                    <div>{`00:1A:${selectedDevice.id.slice(-2)}:${selectedDevice.id.slice(-4, -2)}:${selectedDevice.id.slice(-6, -4)}:${selectedDevice.id.slice(-8, -6)}`}</div>
                    <div className="text-muted-foreground">Connection</div>
                    <div>Mesh</div>
                    <div className="text-muted-foreground">Signal Strength</div>
                    <div>
                      {selectedDevice.status === 'online' ? '85%' : 
                       selectedDevice.status === 'warning' ? '42%' : 'N/A'}
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium mb-1">Usage Information</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">User</div>
                    <div>{selectedDevice.user}</div>
                    <div className="text-muted-foreground">Firmware</div>
                    <div>v2.1.3</div>
                    <div className="text-muted-foreground">Uptime</div>
                    <div>
                      {selectedDevice.status === 'online' ? '3d 7h 22m' : 
                       selectedDevice.status === 'warning' ? '0d 4h 51m' : 'N/A'}
                    </div>
                  </div>
                </div>
                
                {selectedDevice.status === 'warning' && (
                  <div className="bg-yellow-50 dark:bg-yellow-950/20 p-3 rounded-md flex gap-2 items-start border border-yellow-200 dark:border-yellow-900">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-500">Warning</h4>
                      <p className="text-xs text-yellow-700 dark:text-yellow-400">
                        This device is experiencing connectivity issues. Check the signal strength and network configuration.
                      </p>
                    </div>
                  </div>
                )}
                
                {selectedDevice.status === 'offline' && (
                  <div className="bg-red-50 dark:bg-red-950/20 p-3 rounded-md flex gap-2 items-start border border-red-200 dark:border-red-900">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-500 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800 dark:text-red-500">Offline</h4>
                      <p className="text-xs text-red-700 dark:text-red-400">
                        This device is currently offline. Last seen {selectedDevice.lastSeen}. Try restarting the device.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsDetailsSheetOpen(false);
                    // Use setTimeout to ensure proper modal state transitions
                    setTimeout(() => {
                      handleConfigure(selectedDevice);
                    }, 100);
                  }}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Configure
                </Button>
                <SheetClose asChild>
                  <Button>Close</Button>
                </SheetClose>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DeviceTable;
